import {includes, keys, set} from 'lodash';

import type {PeriodFilter} from '/component/screen/report/Report.types';
import * as ALL_REPORTS from '/enum/records.enum';
import {
  REPORT_EARNED_ACCELERATOR_BONUS,
  REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_ELIGIBLE_ACCELERATOR_BONUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
  REPORTS_WITHOUT_PAGINATION,
} from '/enum/records.enum';
import {REPORT_STATE_IS_LOADING} from '/enum/reportState.enum';
import {request} from '/redux/api/request';
import {countByReportName} from '/redux/selector/metric.allReport.selector';
import {createPromisedAction} from '/util/action.util';

import {getErrorHandler} from './apiError.action';

export const FETCH: string = 'REPORT/FETCH';
export const FETCH_RECORD: string = 'REPORT/FETCH_RECORD';
export const SET_STATE: string = 'REPORT/SET_STATE';
export const RESET: string = 'REPORT/RESET';
export const RESET_REPORT: string = 'RESET_REPORT';
export const RESET_REPORT_META: string = 'RESET_REPORT_META';
export const RESET_REFERRAL: string = 'RESET_REFERRAL';

export const AllCandidatesReduxKeys = [
  'REPORT_ALL_CANDIDATES-dateAdded-DESC-default--Filters-N/A',
  'REPORT_ALL_CANDIDATES-dateAdded-ASC-default--Filters-N/A',
  'REPORT_ALL_CANDIDATES-candidateDisposition-ASC-default--Filters-N/A',
];

export const AllReferralReduxKeys = [
  'REPORT_ALL_REFERRALS-dateAdded-DESC-default--Filters-N/A',
  'REPORT_ALL_REFERRALS-dateAdded-ASC-default--Filters-N/A',
];

export const MyteamsReduxKeys = [
  'MY_TEAMS-rankDesc-ASC-default--Filters-N/A',
  'MY_TEAMS-firstNameAZ-ASC-default--Filters-N/A',
  'MY_TEAMS-firstNameZA-DESC-default--Filters-N/A',
  'MY_TEAMS-lastNameAZ-ASC-default--Filters-N/A',
  'MY_TEAMS-lastNameZA-DESC-default--Filters-N/A',
  'MY_TEAMS-rankDesc-DESC-default--Filters-N/A',
  'MY_TEAMS-activationDateNew-ASC-default--Filters-N/A',
  'MY_TEAMS-activationDateOld-DESC-default--Filters-N/A',
];
const fetchCSBPRecords = [
  REPORT_ELIGIBLE_CLIENT_SUPPORT_ONE_TWO,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_THREE_FOUR,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_FIVE_SIX,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_SEVEN_NINE,
  REPORT_EARNED_CLIENT_SUPPORT_BONUS_PLUS,
  REPORT_ELIGIBLE_CLIENT_SUPPORT_BONUS_PLUS,
];
const fetchCABRecords = [
  REPORT_ELIGIBLE_ACCELERATOR_BONUS,
  REPORT_EARNED_ACCELERATOR_BONUS,
];

export const fetchRecord = createPromisedAction(
  FETCH_RECORD,
  ['recordsName', 'recordsParams', 'recordParams', 'recordExtras'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {recordsName, recordsParams, recordParams, recordExtras} =
      inputPayload;
    if (includes(keys(ALL_REPORTS), recordsName)) {
      const fetchRecordData = () => {
        let bonusProgramId;
        if (fetchCSBPRecords.includes(recordsName)) {
          bonusProgramId = 'CSBP';
        } else if (fetchCABRecords.includes(recordsName)) {
          bonusProgramId = 'CAB';
        }
        return request(`${recordsName}_RECORD`, {
          body: {
            ...(recordsParams || []).reduce(
              // @ts-expect-error TS(7006): Parameter 'acc' implicitly has an 'any' type.
              (acc, item) => ({
                ...acc,
                ...item,
              }),
              {},
            ),
            id: recordParams[0],
            ...(bonusProgramId ? {bonusProgramId} : {}),
          },
        }).then(res => ({recordData: res}));
      };
      const fetchExtraData = () =>
        keys(recordExtras).map((extraName: any) => {
          const {endpoint, params} = recordExtras[extraName] || {};
          return request(endpoint, {body: {id: params[0]}}).then(res => {
            return {[`recordExtra.${extraName}`]: res};
          });
        });

      return (
        Promise.all([fetchRecordData(), ...fetchExtraData()])
          // @ts-expect-error TS(2322): Type '[]' is not assignable to type '[{ recordData... Remove this comment to see the full error message
          .then((results = []) => {
            const dataPayloads = {};

            results.forEach(result => {
              Object.keys(result).forEach(key => {
                // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
                set(dataPayloads, key, result[key]);
              });
            });

            return dispatchMe(() => ({
              recordsName,
              recordsParams,
              recordParams,
              ...dataPayloads,
            }));
          })
          .catch(
            // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
            getErrorHandler(dispatch, {
              promisedAction: fetchRecord,
              params: inputPayload,
            }),
          )
      );
    } else {
      return Promise.reject(new Error(`invalid report name: ${recordsName}`));
    }
  },
);

export const fetch = createPromisedAction(
  FETCH,
  ['reportName', 'reportParams', 'appendData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any, getState?: any) => {
    const {reportName, reportParams, appendData} = inputPayload;
    if (includes(keys(ALL_REPORTS), reportName)) {
      // Conditionally inject bonusProgramId
      if (fetchCSBPRecords.includes(reportName)) {
        if (reportParams && reportParams.length > 0) {
          reportParams[0] = {
            ...reportParams[0],
            bonusProgramId: 'CSBP',
          };
        }
      }
      if (fetchCABRecords.includes(reportName)) {
        if (reportParams && reportParams.length > 0) {
          reportParams[0] = {
            ...reportParams[0],
            bonusProgramId: 'CAB',
          };
        }
      }
      return (
        Promise.resolve()
          .then(() =>
            // Before fetching report data, first set the "isLoading" state to true.
            dispatch(
              setState({
                reportName,
                reportParams,
                stateName: REPORT_STATE_IS_LOADING,
                stateValue: true,
              }),
            ),
          )
          .then(() =>
            request(reportName, {
              body: (reportParams || []).reduce(
                // @ts-expect-error TS(7006): Parameter 'acc' implicitly has an 'any' type.
                (acc, item) => ({
                  ...acc,
                  ...item,
                }),
                {},
              ),
              getHeaders: ['report-count'],
            }),
          )
          // @ts-expect-error TS(2339): Property 'data' does not exist on type '{ data: nu... Remove this comment to see the full error message
          .then(({data, responseHeaders}) => {
            // Call `dispatchMe` to dispatch this action with fetched report data.

            dispatchMe(() => {
              const {pageNumber, pageSize} = reportParams[0];
              // const responseHeaderTotal = responseHeaders[0];

              // const total =
              //   responseHeaderTotal ||
              //   countByReportName(getState(), {reportName});

              const [filteredTotal] = responseHeaders;
              const total =
                filteredTotal ?? countByReportName(getState(), {reportName});
              let isEndReached;

              if (data?.length < pageSize) {
                isEndReached = true;
              } else {
                const countOfPages = Math.ceil(total / pageSize);
                // since we don't have a final digit for some reports in order to
                // implement pagination correctly, we need additional verification here
                isEndReached = includes(
                  keys(REPORTS_WITHOUT_PAGINATION),
                  reportName,
                )
                  ? pageNumber > countOfPages
                  : pageNumber >= countOfPages;
              }
              return {
                reportName,
                reportParams,
                appendData,
                reportData: data,
                reportMeta: {
                  isEndReached,
                  //
                  // See "ClientsListHeader" and "CoachesListHeader" components.
                  //
                  // https://medifast.atlassian.net/browse/CONNAPP-822
                  //
                  // TODO: uncomment these lines to show coach/client count at Report List screens
                  page: {
                    total,
                  },
                },
              };
            });
          })
          .then(() =>
            // Finally set the "isLoading" state back to false.
            dispatch(
              setState({
                reportName,
                reportParams,
                stateName: REPORT_STATE_IS_LOADING,
                stateValue: false,
              }),
            ),
          )
          .catch(
            // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
            getErrorHandler(dispatch, {
              promisedAction: fetch,
              params: inputPayload,
            }),
          )
      );
    } else {
      return Promise.reject(new Error(`invalid report name: ${reportName}`));
    }
  },
);

export const allOrders = createPromisedAction(
  FETCH,
  ['reportName', 'reportParams', 'appendData', 'level'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any, getState?: any) => {
    const {
      reportName,
      reportParams,
      appendData,
      level,
      products,
      accountStatus,
    } = inputPayload;
    if (includes(keys(ALL_REPORTS), reportName)) {
      return (
        Promise.resolve()
          .then(() =>
            // Before fetching report data, first set the "isLoading" state to true.
            dispatch(
              setState({
                reportName,
                reportParams,
                stateName: REPORT_STATE_IS_LOADING,
                stateValue: true,
                filtersData: {level, products, accountStatus},
              }),
            ),
          )
          .then(() =>
            request(reportName, {
              body: (reportParams || []).reduce(
                // @ts-expect-error TS(7006): Parameter 'acc' implicitly has an 'any' type.
                (acc, item) => ({
                  ...acc,
                  ...item,
                  level,
                  products,
                  accountStatus,
                }),
                {},
              ),
              getHeaders: ['report-count'],
            }),
          )
          // @ts-expect-error TS(2339): Property 'data' does not exist on type '{ data: nu... Remove this comment to see the full error message
          .then(({data, responseHeaders}) =>
            // Call `dispatchMe` to dispatch this action with fetched report data.
            dispatchMe(() => {
              const {pageNumber, pageSize} = reportParams[0];
              const responseHeaderTotal = responseHeaders[0];

              const total =
                responseHeaderTotal ??
                countByReportName(getState(), {reportName});
              let isEndReached;

              if (data?.length < pageSize) {
                isEndReached = true;
              } else {
                const countOfPages = Math.ceil(total / pageSize);
                isEndReached = includes(
                  keys(REPORTS_WITHOUT_PAGINATION),
                  reportName,
                )
                  ? pageNumber > countOfPages
                  : pageNumber >= countOfPages;
              }

              return {
                reportName,
                reportParams,
                appendData,
                reportData: data,
                reportMeta: {
                  isEndReached,
                  levelFilter: level,
                  productsFilter: products,
                  accountStatusFilter: accountStatus,
                  page: {
                    total,
                  },
                },
                filtersData: {level, products, accountStatus},
              };
            }),
          )
          .then(() =>
            // Finally set the "isLoading" state back to false.
            dispatch(
              setState({
                reportName,
                reportParams,
                stateName: REPORT_STATE_IS_LOADING,
                stateValue: false,
                filtersData: {level, products, accountStatus},
              }),
            ),
          )
          .catch(
            // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
            getErrorHandler(dispatch, {
              promisedAction: allOrders,
              params: inputPayload,
            }),
          )
      );
    } else {
      return Promise.reject(new Error(`invalid report name: ${reportName}`));
    }
  },
);

export const myTeams = createPromisedAction(
  FETCH,
  ['reportName', 'reportParams', 'appendData'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any, getState?: any) => {
    const {
      reportName,
      reportParams,
      appendData,
      rank,
      fibc,
      closeToNextRank,
      frontline,
      tabsValue,
      fqvMin,
      fqvMax,
      newClientsAbove,
      newClientsBelow,
      qualifiedClientsAbove,
      qualifiedClientsBelow,
      newCoachesAbove,
      newCoachesBelow,
    } = inputPayload;

    const rawParams = reportParams?.[0];

    const period: PeriodFilter | undefined = rawParams?.days
      ? {type: 'days', value: rawParams.days}
      : rawParams?.month
      ? {type: 'month', value: rawParams.month}
      : undefined;
    // Extract cabEarned and aabEarned from reportParams[0] if they exist
    const {cabEarned, aabEarned} = reportParams?.[0] || {};
    if (includes(keys(ALL_REPORTS), reportName)) {
      return (
        Promise.resolve()
          .then(() =>
            // Before fetching report data, first set the "isLoading" state to true.
            dispatch(
              setState({
                reportName,
                reportParams,
                stateName: REPORT_STATE_IS_LOADING,
                stateValue: true,
                filtersData: {
                  ...(rank ? {rank} : {}),
                  ...(period ? {period} : {}),
                  fibc,
                  frontline,
                  cabEarned,
                  aabEarned,
                  closeToNextRank,
                  fqvMin,
                  fqvMax,
                  newClientsAbove,
                  newClientsBelow,
                  qualifiedClientsAbove,
                  qualifiedClientsBelow,
                  newCoachesAbove,
                  newCoachesBelow,
                },
              }),
            ),
          )
          .then(() =>
            request(reportName, {
              body: (reportParams || []).reduce(
                // @ts-expect-error TS(7006): Parameter 'acc' implicitly has an 'any' type.
                (acc, item) => ({
                  ...(tabsValue === 'watchlist' ? {isWatchlist: true} : {}),
                  ...acc,
                  ...item,
                  ...(rank ? {rank} : {}), //  Only add rank if it has a value
                  fibc,
                  frontline,
                  ...(fqvMin !== undefined ? {fqvMin} : {}),
                  ...(fqvMax !== undefined ? {fqvMax} : {}),
                  ...(newClientsAbove !== undefined ? {newClientsAbove} : {}),
                  ...(newClientsBelow !== undefined ? {newClientsBelow} : {}),
                  ...(qualifiedClientsAbove !== undefined
                    ? {qualifiedClientsAbove}
                    : {}),
                  ...(qualifiedClientsBelow !== undefined
                    ? {qualifiedClientsBelow}
                    : {}),
                  ...(newCoachesAbove !== undefined ? {newCoachesAbove} : {}),
                  ...(newCoachesBelow !== undefined ? {newCoachesBelow} : {}),
                  ...(cabEarned ? {cabEarned} : {}),
                  ...(aabEarned ? {aabEarned} : {}),
                  closeToNextRank,
                }),
                {},
              ),
              getHeaders: ['report-count', 'report-count-allorg'],
            }),
          )
          // @ts-expect-error TS(2339): Property 'data' does not exist on type '{ data: nu... Remove this comment to see the full error message
          .then(({data, responseHeaders}) =>
            // Call `dispatchMe` to dispatch this action with fetched report data.
            dispatchMe(() => {
              const {pageNumber, pageSize} = reportParams[0];
              // const responseHeaderTotal = responseHeaders[0];
              // const reportcount = responseHeaders[1];
              // const filtercount =
              //   responseHeaderTotal ||
              //   countByReportName(getState(), {reportName});
              // const total =
              //   reportcount || countByReportName(getState(), {reportName});

              const [filteredTotal, allOrgTotal] = responseHeaders;
              const filtercount =
                filteredTotal || countByReportName(getState(), {reportName});
              const total =
                allOrgTotal || countByReportName(getState(), {reportName});

              let isEndReached;
              if (data?.length < pageSize) {
                isEndReached = true;
              } else {
                const countOfPages = Math.ceil(total / pageSize);
                isEndReached = includes(
                  keys(REPORTS_WITHOUT_PAGINATION),
                  reportName,
                )
                  ? pageNumber > countOfPages
                  : pageNumber >= countOfPages;
              }
              return {
                reportName,
                reportParams,
                appendData,
                reportData: data,
                reportMeta: {
                  isEndReached,
                  ...(rank ? {rankFilter: rank} : {}),
                  periodFilter: period,
                  fibcFilter: fibc,
                  frontlineFilter: frontline,
                  cabEarned,
                  aabEarned,
                  nextRankFilter: closeToNextRank,
                  fqvMin,
                  fqvMax,
                  newClientsAbove,
                  newClientsBelow,
                  qualifiedClientsAbove,
                  qualifiedClientsBelow,
                  newCoachesAbove,
                  newCoachesBelow,
                  page: {
                    filtercount,
                  },
                  report: {
                    total,
                  },
                },
                filtersData: {
                  ...(rank ? {rank} : {}),
                  period,
                  fibc,
                  frontline,
                  cabEarned,
                  aabEarned,
                  closeToNextRank,
                  fqvMin,
                  fqvMax,
                  newClientsAbove,
                  newClientsBelow,
                  qualifiedClientsAbove,
                  qualifiedClientsBelow,
                  newCoachesAbove,
                  newCoachesBelow,
                },
              };
            }),
          )
          .then(() =>
            // Finally set the "isLoading" state back to false.
            dispatch(
              setState({
                reportName,
                reportParams,
                stateName: REPORT_STATE_IS_LOADING,
                stateValue: false,
                filtersData: {
                  ...(rank ? {rank} : {}),
                  period,
                  fibc,
                  frontline,
                  cabEarned,
                  aabEarned,
                  closeToNextRank,
                  fqvMin,
                  fqvMax,
                  newClientsAbove,
                  newClientsBelow,
                  qualifiedClientsAbove,
                  qualifiedClientsBelow,
                  newCoachesAbove,
                  newCoachesBelow,
                },
              }),
            ),
          )
          .catch(
            // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
            getErrorHandler(dispatch, {
              promisedAction: myTeams,
              params: inputPayload,
            }),
          )
      );
    } else {
      return Promise.reject(new Error(`invalid report name: ${reportName}`));
    }
  },
);
export const setState = createPromisedAction(SET_STATE, [
  'reportName',
  'reportParams',
  'stateName',
  'stateValue',
  'filtersData',
]);

export const reset = createPromisedAction(RESET, []);

export const resetReport = createPromisedAction(RESET_REPORT, [
  'reportKey',
  'data',
]);
export const resetReportMeta = createPromisedAction(RESET_REPORT_META, [
  'reportKey',
  'data',
]);

export const resetReferralReport = createPromisedAction(RESET_REFERRAL, [
  'reportKey',
  'data',
]);
